<template>
  <v-data-table :headers="headers" :items="items.existing" :value="value.existing" @input="onInput">
    <template v-slot:top>
      <v-toolbar flat color="white">
        <v-toolbar-title>{{ label }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-dialog persistent v-model="participantsToAddDialog" v-if="!disabled" width="auto">
          <template v-slot:activator="{ on: onDialog, attrs: attrsDialog }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: onTooltip, attrs: attrsTooltip }">
                <v-btn
                  class="ml-2"
                  fab
                  small
                  v-bind="{ ...attrsDialog, ...attrsTooltip }"
                  v-on="{ ...onDialog, ...onTooltip }"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
              <span>{{ $i18n.translate("Add") }} {{ $i18n.translate("Participant") }}</span>
            </v-tooltip>
          </template>
          <v-card>
            <v-card-title>
              <span class="headline">Add Participants</span>
              <v-spacer> </v-spacer>
              <v-form @submit.stop.prevent="onParticipantsToAddFilter">
                <v-text-field
                  class="mr-4"
                  v-model="participantsToAdd.search"
                  :label="$i18n.translate('Filter') + ' ' + $i18n.translate('Participants')"
                  flat
                  solo-inverted
                  hide-details
                  clearable
                  clear-icon="mdi-close-circle-outline"
                  @click:clear="onParticipantsToAddClearFilter"
                ></v-text-field>
              </v-form>
              <v-menu offset-y :close-on-content-click="false">
                <template v-slot:activator="{ on: onMenu }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on: onTooltip }">
                      <v-btn icon v-on="{ ...onMenu, ...onTooltip }">
                        <v-icon>mdi-magnify</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $i18n.translate("Advanced Search") }}</span>
                  </v-tooltip>
                </template>
                <v-list>
                  <v-list-item-group color="primary">
                    <v-subheader>{{ $i18n.translate("Advanced Search") }}</v-subheader>
                    <v-list-item selectable>
                      <v-list-item-content>
                        <ParticipantTypesField
                          dense
                          v-model="participantsToAdd.filterByParticipantTypes"
                          label="Participant Types"
                          @input="getParticipantsToAddData"
                        ></ParticipantTypesField>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list-item-group>
                </v-list>
              </v-menu>
              <v-tooltip bottom v-if="hasFilters">
                <template v-slot:activator="{ on: onTooltip }">
                  <v-btn icon v-on="{ ...onTooltip }" @click="onParticipantsToAddClearSearch">
                    <v-icon>mdi-magnify-close</v-icon>
                  </v-btn>
                </template>
                <span>{{ $i18n.translate("Clear Search") }}</span>
              </v-tooltip>
            </v-card-title>

            <v-card-text>
              <v-data-table
                v-model="participantsToAdd.selected"
                show-select
                :items="participantsToAdd.items"
                :headers="participantsToAdd.headers"
                :server-items-length="participantsToAdd.total"
                :options.sync="participantsToAddOptions"
                :loading="participantsToAdd.loading"
              >
                <template v-slot:item.organization="{ item }">
                  <span v-if="item.organization">
                    {{ item.organization.name }}
                  </span>
                </template>
                <template v-slot:item.updatedDate="{ item }">
                  {{ item.updatedDate | formatDateFromNow }}
                </template>
              </v-data-table>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn @click="onParticipantsToAddClose">{{ $i18n.translate("Cancel") }}</v-btn>
              <v-btn color="primary" @click="onParticipantsToAddIncludeSubmit">Include</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-toolbar>
    </template>
    <template v-slot:item.participant.organization="{ item }">
      <span v-if="item.participant.organization">
        {{ item.participant.organization.name }}
      </span>
    </template>
    <template v-slot:item.actions="{ item }">
      <v-icon v-if="!disabled" @click="onDeleteItem(item)">
        mdi-delete
      </v-icon>
    </template>
  </v-data-table>
</template>

<script>
import ApiService from "../../services/api.service";
import ParticipantTypesField from "../fields/ParticipantTypesField";

export default {
  name: "ParticipantsTable",
  props: {
    value: Object,
    disabled: Boolean,
    label: {
      type: String,
      required: false,
      default: "Include Specific Participants"
    }
  },
  components: {
    ParticipantTypesField
  },
  data: () => ({
    valid: false,
    loaded: false,
    participantsToAddDialog: false,
    headers: [
      { text: "Key", value: "participantKey" },
      { text: "Email", value: "email1" },
      { text: "Name", value: "fullName" },
      { text: "Company", value: "organizationName" },
      { text: "Type", value: "participantTypeName", align: "end" },
      { text: "Actions", value: "actions", sortable: false }
    ],
    items: {
      existing: [],
      deleted: []
    },
    editedIndex: -1,
    editedItem: {},
    editedPromotionParticipantPayouts: [],
    defaultItem: {},

    participantsToAdd: {
      search: "",
      filterByParticipantTypes: [],
      loading: false,
      selected: [],
      items: [],
      nameFilter: "",
      headers: [
        {
          value: "participantKey",
          text: "Key",
          sortable: true
        },
        {
          value: "user.firstName",
          text: "First Name",
          sortable: true
        },
        {
          value: "user.lastName",
          text: "Last Name",
          sortable: true
        },
        {
          value: "email1",
          text: "Email",
          sortable: true
        },
        {
          value: "organization.organizationKey",
          text: "Company Number",
          sortable: true
        },
        {
          value: "organization",
          text: "Company",
          sortable: true
        },
        {
          value: "participantType.name",
          text: "Participant Type",
          align: "end",
          sortable: true
        },
        {
          value: "updatedDate",
          text: "Last Updated",
          sortable: true
        }
      ],
      total: 0
    },
    participantsToAddOptions: {
      itemsPerPage: 10
    }
  }),
  watch: {
    participantsToAddOptions: {
      handler() {
        this.getParticipantsToAddData();
      },
      deep: true
    },
    value: {
      handler() {
        this.initialize();
      },
      deep: true
    }
  },

  created() {
    this.initialize();
  },

  methods: {
    initialize() {
      if (this.value.existing) {
        this.items.existing = this.value.existing;
        this.$emit("input", this.items);
        this.loaded = true;
      }
    },
    onInput() {
      this.$emit("input", this.items);
    },

    onDeleteItem(item) {
      const index = this.items.existing.indexOf(item);
      let deletedItems = this.items.existing.splice(index, 1);
      deletedItems = deletedItems.reduce((acc, cur) => {
        if (cur.id) {
          acc.push(cur);
        }
        return acc;
      }, []);
      this.items.deleted = this.items.deleted.concat(deletedItems);
      this.$emit("input", this.items);
      if (this.loaded) {
        this.$emit("change");
      }
    },

    onParticipantsToAddFilter() {
      this.participantsToAddOptions.page = 0;
      this.getParticipantsToAddData();
    },

    onParticipantsToAddClearFilter() {
      this.participantsToAdd.search = "";
      this.getParticipantsToAddData();
    },
    onParticipantsToAddClearSearch() {
      this.participantsToAdd.search = "";
      this.participantsToAdd.filterByParticipantTypes = [];
      this.getParticipantsToAddData();
    },

    onParticipantsToAddIncludeSubmit() {
      let promotionParticipants = this.items.existing.slice();
      this.participantsToAdd.selected.some(participant => {
        let found = promotionParticipants.findIndex(needle => needle.id == participant.id);
        if (found == -1) {
          promotionParticipants = promotionParticipants.concat(participant);
        }
      });
      this.items.existing = promotionParticipants;
      this.participantsToAddDialog = false;
      this.onParticipantsToAddClearFilter();
      this.onParticipantsToAddClearSearch();
      this.participantsToAdd.selected = [];
      this.$emit("input", this.items);
      if (this.loaded) {
        this.$emit("change");
      }
    },

    onParticipantsToAddClose() {
      this.participantsToAddDialog = false;
      this.onParticipantsToAddClearFilter();
      this.onParticipantsToAddClearSearch();
      this.participantsToAdd.selected = [];
      this.onParticipantsToAddClearFilter();
    },

    getParticipantsToAddData() {
      this.participantsToAdd.loading = true;
      const { sortBy, sortDesc, page, itemsPerPage } = this.participantsToAddOptions;

      let filters = {};
      if (this.participantsToAdd.search && this.participantsToAdd.search.length > 0) {
        filters.keyword = this.participantsToAdd.search;
      }

      if (
        this.participantsToAdd.filterByParticipantTypes &&
        this.participantsToAdd.filterByParticipantTypes.length > 0
      ) {
        filters.participantTypes = this.participantsToAdd.filterByParticipantTypes.map(participantType => {
          return {
            id: participantType.id
          };
        });
      }
      ApiService.post(
        "/api/participants/search?size=" +
          itemsPerPage +
          "&page=" +
          (page - 1) +
          (sortBy && sortBy.length > 0
            ? "&sort=" + sortBy[0] + "," + ((sortDesc && sortDesc.length > 0) & sortDesc[0] ? "DESC" : "ASC")
            : ""),
        filters
      )
        .then(({ data }) => {
          this.participantsToAdd.loading = false;
          this.participantsToAdd.items = data.content;
          this.participantsToAdd.total = data.totalElements;
        })
        .catch(() => {
          this.participantsToAdd.loading = false;
        });
    }
  },
  computed: {
    hasFilters() {
      let check =
        (this.search && this.search.length > 0) ||
        (this.participantsToAdd.filterByParticipantTypes && this.participantsToAdd.filterByParticipantTypes.length > 0);
      return check;
    }
  }
};
</script>
